// Mobile-only horizontal chip strip — the bottom-anchored equivalent of
// the rail's viewport-filtered glossary list. Same `data-term` /
// `data-term-id` / `data-term-family` contract as inline marks and rail
// rows so the existing family-color CSS applies without per-element
// overrides. The strip is rendered always; CSS gates visibility on
// (max-width: 768px). See reading-page.css.
//
// Active-chip auto-scroll uses Element.scrollTo on the strip itself —
// never scrollIntoView — per the origin reference's eighth trap
// (scrollIntoView propagates to ancestors and yanks the article scroll).

import { useEffect, useRef } from "react";
import type { PublishedTerm } from "../data/types";
import { resolveTermFamily } from "../reading/termFamily";

export interface ChipStripProps {
  terms: PublishedTerm[];
  selectedTermId: string | null;
  onSelect: (term: PublishedTerm) => void;
}

export function ChipStrip({ terms, selectedTermId, onSelect }: ChipStripProps) {
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedTermId) return;
    const strip = stripRef.current;
    if (!strip) return;
    const chip = strip.querySelector<HTMLElement>(
      `[data-term-id="${CSS.escape(selectedTermId)}"]`,
    );
    if (!chip) return;
    const stripRect = strip.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const margin = 12;
    if (chipRect.left < stripRect.left + margin) {
      strip.scrollTo({
        left: strip.scrollLeft + (chipRect.left - stripRect.left) - margin,
        behavior: "smooth",
      });
    } else if (chipRect.right > stripRect.right - margin) {
      strip.scrollTo({
        left: strip.scrollLeft + (chipRect.right - stripRect.right) + margin,
        behavior: "smooth",
      });
    }
  }, [selectedTermId]);

  return (
    <div
      ref={stripRef}
      className="maisight-chip-strip"
      role="list"
      aria-label="Glossary terms in view"
    >
      {terms.length === 0 ? (
        <span className="maisight-chip-strip-empty">No terms in view</span>
      ) : (
        terms.map((t) => (
          <button
            key={t.term_id}
            type="button"
            role="listitem"
            className={`maisight-chip${selectedTermId === t.term_id ? " is-selected" : ""}`}
            data-term={t.term}
            data-term-id={t.term_id}
            data-term-family={resolveTermFamily(t)}
            onClick={() => onSelect(t)}
          >
            {t.term}
          </button>
        ))
      )}
    </div>
  );
}
